import React, { ReactNode, forwardRef, useEffect, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';
import {
  BackgroundProps,
  BorderRadiusProps,
  BoxShadowProps,
  ColorProps,
  LayoutProps,
  PaddingProps,
  PositionProps,
  TextAlignProps,
} from 'styled-system';

import Box from 'components/Box';
import {
  Button,
  CloseButton,
  PrimaryButton,
  TrackedPrimaryButtonLink,
} from 'components/Button';
import Form, { LocationField } from 'components/Form';
import Hide from 'components/Hide';
import Icon from 'components/Icon';
import { Image } from 'components/Image';
import ShareListingButton from 'components/ShareListingButton';
import Spinner from 'components/Spinner';
import { BodySmall, H2, H5, RichText } from 'components/Text';
import {
  bodyTextBase,
  breakpoints,
  colors,
  fontWeights,
  gutterWidth,
  transition,
} from 'theme';
import { IDEALIST_DAY_EVENT_DATES } from 'utils/constants';
import { shortMonth, shortestDate, yearOnly } from 'utils/date';
import { isDefined } from 'utils/functional';

const PADDING = 16;
const buttonHoverStyles = css`
  &:hover {
    &:not([disabled]) {
      background: white;
      box-shadow: 0 0 1px rgba(0, 0, 0, 0.4) !important;
      color: ${colors.battleshipGrey};
    }
  }
`;

const aPath = (amount) => keyframes`
  0% {
    stroke-dashoffset: -${amount}px;
  }
  30%, 70% {
    stroke-dashoffset: 0;
  }
  100% {
    stroke-dashoffset: ${amount}px;
  }
`;

const BaseLocationField = styled(LocationField)`
  @media (min-width: ${breakpoints[0]}) {
    padding-left: 44px !important;
    background-image: url('/static/images/icons/location-alt.svg');
    background-repeat: no-repeat;
    background-position: 12px center;
    background-size: 20px 20px;
  }
`;
const MapLocationField = styled(BaseLocationField)`
  height: 40px;
  width: 100%;
  margin-bottom: 0;
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.3);
  border: 0;
  border-radius: 10px;

  &:focus {
    box-shadow: 0 1px 6px rgba(0, 0, 0, 0.3), 0 0 0 1px #0d73d9,
      0 0 0 1px #0d73d9 inset;
  }

  @media (min-width: ${breakpoints[2]}) {
    padding: 16px;
    max-height: none;
    width: 100%;
  }

  input {
    width: 100%;
  }
`;
const InfoBoxLocationField = styled(BaseLocationField)`
  background-color: ${colors.platinum};
`;
const StyledImg = styled(Image)<{ layout?: string }>`
  flex: 0 0 auto;
`;
const SelectDateButton = styled(
  ({
    className,
    date,
    setShowInfoBox,
    setShowDates,
    showInfoBox,
  }: {
    className?: string;
    date: string;
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setShowInfoBox: (...args: Array<any>) => any;
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setShowDates: (...args: Array<any>) => any;
    showInfoBox: boolean;
  }) => (
    <Box
      component={Button}
      data-qa-id="select-idealist-day-menu-button"
      onClick={(e) => {
        setShowDates(true, e);

        if (showInfoBox) {
          setShowInfoBox(false);
        }
      }}
      display="flex"
      alignItems="stretch"
      overflow="hidden"
      className={className}
    >
      <Box bg={colors.marigold} p={11}>
        <Icon name="calendar" display="block" width={18} height={18} />
      </Box>
      <Box px={10} bg="white" display="flex" alignItems="center">
        <BodySmall>{shortestDate(date)}</BodySmall>
      </Box>
    </Box>
  )
)`
  border-radius: 10px;
  background: white;
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.3);
  transition: all ${transition};
  ${buttonHoverStyles}
`;
const DateButton = styled(
  ({
    className,
    onClick,
    children,
    ...props
  }: {
    className?: string;
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onClick: (...args: Array<any>) => any;
    children: ReactNode;
    'data-qa-id': string;
  }) => (
    <Box data-qa-id={props['data-qa-id']} className={className}>
      <Button onClick={onClick}>{children}</Button>
    </Box>
  )
)<{ current?: boolean }>`
  flex: 1 0 50%;
  max-height: 232px;
  height: 100%;
  padding: 0 16px;

  button {
    background: ${colors.white};
    border-radius: 20px;
    box-shadow: 0 1px 5px rgba(0, 0, 0, 0.15);
    position: relative;
    height: 100%;
    text-align: center;
    transition: all ${transition};
    width: 100%;

    ${({ current }) =>
      current &&
      `
      border: solid 6px ${colors.marigold};
    `}

    &:before {
      background: ${colors.marigold};
      border-radius: 4px;
      content: '';
      height: 10px;
      width: 30px;
      left: calc(50% - 30px / 2);
      position: absolute;
      top: 20px;
    }

    &:hover {
      background: ${colors.waterBlue};
      border-color: ${colors.waterBlue};
      box-shadow: 0 1px 1px rgba(0, 0, 0, 0.15);
      color: ${colors.white};

      &:before {
        background: ${colors.white};
      }
    }
  }

  @media (min-width: ${breakpoints[2]}) {
    flex-basis: 25%;
    padding: 16px;
    max-height: none;
  }
`;

// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line react/function-component-definition
const Dates = ({
  setDate,
  activeDate,
}: {
  setDate: (date: string, event: MouseEvent) => void;
  activeDate: string;
}) => (
  <Box
    data-qa-id="idealist-days-map-select-idealist-day"
    height="100%"
    width="100%"
    position="absolute"
    top="0"
    left="0"
  >
    <Box display="flex" flexWrap="wrap" height="100%">
      {IDEALIST_DAY_EVENT_DATES.slice(0, 4).map((date, index) => (
        <DateButton
          current={date.id === activeDate}
          data-qa-id={`idealist-day-button-${index}`}
          key={date.id}
          onClick={(e) => setDate(date.id, e)}
        >
          <H5 fontWeight={fontWeights.normal} letterSpacing="2.5px" m={0}>
            {date.name}
          </H5>
          <H2 m={0}>{shortMonth(date.id).toUpperCase()}</H2>
          <H5 fontWeight={fontWeights.normal} letterSpacing="2.5px" m={0}>
            {yearOnly(date.id)}
          </H5>
        </DateButton>
      ))}
    </Box>
  </Box>
);

const InfoBox = styled(
  ({
    className,
    instructions,
    buttonText,
    setShowInfoBox,
    selectAddress,
    mapType,
    initialValues,
    ...props
  }: {
    className?: string;
    instructions: string;
    buttonText: string;
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setShowInfoBox: (...args: Array<any>) => any;
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    selectAddress: (...args: Array<any>) => any;
    mapType: string;
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    initialValues: Record<string, any>;
  }) => (
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line react/jsx-props-no-spreading
    <Box className={className} {...props}>
      <Box data-qa-id="idealist-days-map-info-box">
        <Box
          position="absolute"
          top={[0, null, null, '10px']}
          right={[0, null, null, '10px']}
        >
          <CloseButton
            data-qa-id="idealist-days-map-info-box-close"
            onClick={() => setShowInfoBox(false)}
          />
        </Box>

        <RichText
          html={instructions}
          data-qa-id="idealist-days-map-info-box-body"
        />

        {/* @ts-expect-error ts-migrate(2695) FIXME: Left side of comma operator is unused and has no s... Remove this comment to see the full error message */}
        <Box mt={(16, null, gutterWidth / 2)} mb={[16, null, gutterWidth]}>
          <Form
            data-qa-id="idealist-days-map-info-box-form"
            initialValues={initialValues}
            // @ts-expect-error ts-migrate(2769) FIXME: No overload matches this call.
            onSubmit={() => {
              // do nothing
            }}
            render={() => (
              // TODO: Fix this the next time the file is edited.
              // eslint-disable-next-line react/jsx-no-useless-fragment
              <>
                <InfoBoxLocationField
                  name="address"
                  aria-label={getText('Map search location')}
                  placeholder={getText('Search Location')}
                  addressTypes={['geocode']}
                  onChange={({ latitude, longitude, latLongAddress }) => {
                    selectAddress({
                      latitude,
                      longitude,
                      latLongAddress,
                    });
                  }}
                />
              </>
            )}
          />
        </Box>

        <PrimaryButton
          width={[1, null, 'auto']}
          data-qa-id="idealist-days-map-info-box-cta"
          onClick={() => setShowInfoBox(false)}
        >
          {buttonText ||
            (mapType === 'DAYS'
              ? getText('Explore Idealist Days')
              : getText('Explore Groups'))}
        </PrimaryButton>
      </Box>
    </Box>
  )
)<
  PositionProps &
    LayoutProps &
    PaddingProps &
    BackgroundProps &
    ColorProps &
    BorderRadiusProps &
    BoxShadowProps &
    TextAlignProps & { transform?: string }
>`
  h2 {
    &:before {
      height: 0;
      margin: 0;
    }
  }
`;
const MapSpinner = styled((props) => (
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line react/jsx-props-no-spreading
  <Box {...props}>
    <Icon name="map" width={64} height={64} />
  </Box>
))`
  svg {
    * {
      stroke: ${colors.silver};
    }
    polyline {
      animation: ${aPath(167)} 2s infinite;
      stroke-dasharray: 167px;
      stroke-dashoffset: -167px;
    }
    path,
    circle,
    polyline {
      fill: ${colors.fog};
    }
    path,
    polyline {
      stroke-dasharray: 167px;
      stroke-dashoffset: -167px;
      animation: ${aPath(167)} 2s infinite;
    }
    circle {
      stroke-dasharray: 30px;
      stroke-dashoffset: -30px;
      animation: ${aPath(30)} 2s infinite;
    }
  }
`;
// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const StyledShareListingButton: any = styled(ShareListingButton)`
  margin-right: 8px;
  height: 40px;
  align-items: center;
  flex-direction: row-reverse;
  border-radius: 10px;
  background: white;
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.3);
  > *:first-child {
    padding-right: 0;
  }
  ${({ iconOnly }) =>
    iconOnly
      ? `
    padding: 11px;
  `
      : `
  > *:first-child {
    padding-left: 5px;
  }`}
  ${buttonHoverStyles}
`;
const StyledButton = styled(Button)`
  padding: 11px;
  border-radius: 10px;
  background: white;
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.3);
  color: ${colors.battleshipGrey};
  -webkit-font-smoothing: antialiased;
  transition: all ${transition};
  ${buttonHoverStyles}
  &:hover {
    svg {
      * {
        transition: all ${transition};
        fill: ${colors.turtleGreen};
      }
    }
  }
`;
const MapWrapper = styled.div.attrs<{ fullScreen?: boolean }>((props) => ({
  style: props.fullScreen
    ? {
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        width: '100%',
        zIndex: 3000,
        background: 'white',
        overflow: 'hidden',
      }
    : {
        position: 'relative',
        minHeight: '500px',
        borderRadius: '4px',
        background: colors.fog,
      },
}))<{ fullScreen?: boolean }>`
  ${(props) =>
    props.fullScreen
      ? `
    height: calc(100% - 60px);
    @media (min-width: ${breakpoints[2]}) {
      height: calc(100% - 40px);
    }
  `
      : `
    height: calc(100vh - 160px);
    }
  `}
  .gm-style {
    .gm-style-iw-d {
      max-width: calc(100vw - 48px) !important;
      overflow: auto !important;
      @media (min-width: ${breakpoints[0]}) {
        max-width: none !important;
      }
    }
    .gm-style-iw-c {
      max-width: calc(100vw - 24px) !important;
      padding: 12px !important;
      background-image: linear-gradient(
        to left,
        ${colors.skyBlue},
        ${colors.niceBlue}
      );
      background-size: 100% 7px;
      background-repeat: no-repeat;
      background-position: left bottom;
      box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
      ${bodyTextBase};
      font-size: 14px;
      line-height: 1.375;
      button[aria-label='Close'] {
        top: 0 !important;
        right: 0 !important;
      }
      @media (min-width: ${breakpoints[0]}) {
        max-width: 528px !important;
        padding: 24px !important;
      }
    }
    .gm-style-iw-t {
      &::after {
        width: 10px;
        height: 10px;
        background: #3175bb;
      }
    }
  }
  .gm-bundled-control-on-bottom {
    right: 46px !important;
    .gmnoprint {
      > * {
        border-radius: 10px !important;
      }
      button {
        &::after {
          content: '';
          position: absolute;
          left: 50%;
          top: 50%;
          transform: translate(-50%, -50%);
          width: 20px;
          height: 20px;
          background-size: 20px 20px;
          background-position: center;
          background-repeat: no-repeat;
        }
        &:first-child {
          &::after {
            background-image: url('/static/images/icons/plus.svg');
          }
        }
        &:last-child {
          &::after {
            background-image: url('/static/images/icons/minus.svg');
          }
        }
        img {
          display: none;
        }
      }
    }
  }
`;
const StyledMap = styled.div`
  height: 100%;
`;

// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line react/function-component-definition
const EventOrGroupCount = ({
  mapType,
  eventOrGroupCount,
}: {
  mapType: string;
  eventOrGroupCount: number | null | undefined;
}) => (
  <Box component={H5} display="flex" alignItems="center" ml={20}>
    {/* @ts-expect-error ts-migrate(2367) FIXME: This condition will always return 'false' since th... Remove this comment to see the full error message */}
    {!eventOrGroupCount && !eventOrGroupCount === 0 ? (
      <Box minWidth={12}>
        <Spinner size="text" />
      </Box>
    ) : (
      eventOrGroupCount
    )}
    <Box as="span" fontWeight="normal" color={colors.battleshipGrey} ml="6px">
      {mapType === 'DAYS' && (
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line react/jsx-no-useless-fragment
        <>{eventOrGroupCount === 1 ? getText('event') : getText('events')}</>
      )}
      {mapType === 'GROUPS' && (
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line react/jsx-no-useless-fragment
        <>{eventOrGroupCount === 1 ? getText('group') : getText('groups')}</>
      )}
    </Box>
  </Box>
);

// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line react/function-component-definition
const UserPinCount = ({
  userPinCount,
  mapType,
}: {
  userPinCount: number | null | undefined;
  mapType: string;
}) => (
  <Box component={H5} display="flex" alignItems="center" ml={20}>
    {/* @ts-expect-error ts-migrate(2367) FIXME: This condition will always return 'false' since th... Remove this comment to see the full error message */}
    {!userPinCount && !userPinCount === 0 ? (
      <Spinner size="micro" />
    ) : (
      userPinCount
    )}
    <Box as="span" fontWeight="normal" color={colors.battleshipGrey} ml="6px">
      {/* TODO: Fix this the next time the file is edited. */}
      {/* eslint-disable-next-line no-nested-ternary */}
      {mapType === 'DAYS'
        ? userPinCount === 1
          ? getText('individual action')
          : getText('individual actions')
        : userPinCount === 1
        ? getText('individual offer')
        : getText('individual offers')}
    </Box>
  </Box>
);

type Props = {
  mapType: string;
  date: string;
  eventOrGroupCount: number | null | undefined;
  fullScreen: boolean;
  instructions: string;
  buttonText: string | null | undefined;
  loaded: boolean;
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  selectAddress: (...args: Array<any>) => any;
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setDate: (...args: Array<any>) => any;
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setShowDates: (...args: Array<any>) => any;
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setShowInfoBox: (...args: Array<any>) => any;
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  shareProps: Record<string, any>;
  showDates: boolean;
  showInfoBox: boolean;
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  toggleFullScreen: (...args: Array<any>) => any;
  userPinCount: number | null | undefined;
  gMapsErrored: boolean;
};
const IdealistDaysMap = forwardRef<Props, HTMLElement>(
  // @ts-expect-error ts-migrate(2345) FIXME: Argument of type '({ mapType, date, eventOrGroupCo... Remove this comment to see the full error message
  (
    {
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line react/prop-types
      mapType,
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line react/prop-types
      date,
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line react/prop-types
      eventOrGroupCount,
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line react/prop-types
      fullScreen,
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line react/prop-types
      instructions,
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line react/prop-types
      buttonText,
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line react/prop-types
      loaded,
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line react/prop-types
      selectAddress,
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line react/prop-types
      setDate,
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line react/prop-types
      setShowDates,
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line react/prop-types
      setShowInfoBox,
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line react/prop-types
      shareProps,
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line react/prop-types
      showDates,
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line react/prop-types
      showInfoBox,
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line react/prop-types
      toggleFullScreen,
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line react/prop-types
      userPinCount,
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line react/prop-types
      gMapsErrored,
    }: Props,
    ref
  ) => {
    const [initialValues, setInitialValues] = useState({
      address: null,
    });
    useEffect(() => {
      const clickHandler = () => {
        setDate();
      };

      window.addEventListener('click', clickHandler);
      return () => {
        window.removeEventListener('click', clickHandler);
      };
    }, [setDate]);

    const setShowDatesHandler = (show: boolean, e: MouseEvent) => {
      setShowDates(show);
      e.stopPropagation();
    };

    const setNewDate = (id: string, e: MouseEvent) => {
      setDate(id);
      e.stopPropagation();
    };

    const onSelectAddress = (address) => {
      selectAddress(address);
      setInitialValues({
        address: address.latLongAddress,
      });
    };

    return (
      <Box>
        <MapWrapper fullScreen={fullScreen} data-qa-id="idealist-day-map">
          <Box
            position="absolute"
            top="50%"
            left="50%"
            textAlign="center"
            transform="translate(-50%, -50%)"
          >
            <MapSpinner />
            {gMapsErrored && (
              <>
                {getText(
                  'Our geolocation provider (Google Maps) did not load correctly. Please reload the page.'
                )}
              </>
            )}
          </Box>
          {/* @ts-expect-error Fix type */}
          <StyledMap ref={ref} />

          {!showInfoBox && (
            <Box>
              <Box
                position="absolute"
                top={PADDING}
                left={PADDING}
                display="flex"
                maxWidth={mapType === 'DAYS' ? 480 : 400}
                width="calc(100% - 128px)"
              >
                <Box mr="8px">
                  <StyledButton
                    data-qa-id="idealist-days-map-show-instructions"
                    onClick={() => {
                      setShowInfoBox(true);
                    }}
                  >
                    <Icon name="info" display="block" width={18} height={18} />
                  </StyledButton>
                </Box>
                {mapType === 'DAYS' && (
                  // @ts-expect-error ts-migrate(2322) FIXME: Type '{ children: Element; xs: true; sm: true; mr:... Remove this comment to see the full error message
                  <Hide xs sm mr="8px">
                    <SelectDateButton
                      date={date}
                      setShowInfoBox={setShowInfoBox}
                      setShowDates={setShowDatesHandler}
                      showInfoBox={showInfoBox}
                    />
                  </Hide>
                )}
                <Box width={1} flex="1 1 auto">
                  <Form
                    data-qa-id="idealist-days-map-location-on-map-form"
                    initialValues={initialValues}
                    // @ts-expect-error ts-migrate(2769) FIXME: No overload matches this call.
                    onSubmit={() => {}}
                    render={() => (
                      // TODO: Fix this the next time the file is edited.
                      // eslint-disable-next-line react/jsx-no-useless-fragment
                      <>
                        <MapLocationField
                          name="address"
                          aria-label={getText('Map search location')}
                          placeholder={getText('Search Location')}
                          addressTypes={['geocode']}
                          onChange={({
                            latitude,
                            longitude,
                            latLongAddress,
                          }) => {
                            selectAddress({
                              latitude,
                              longitude,
                              latLongAddress,
                            });
                          }}
                        />
                      </>
                    )}
                  />
                </Box>
              </Box>

              <Box
                position="absolute"
                top={PADDING}
                right={PADDING}
                display="flex"
                justifyContent="flex-end"
                width={[`calc(100% - ${PADDING * 3}px)`, null, 'auto']}
              >
                <Hide xs>
                  {/* TODO: Fix this the next time the file is edited. */}
                  {/* eslint-disable-next-line react/jsx-props-no-spreading */}
                  <StyledShareListingButton {...shareProps} />
                </Hide>
                <Hide sm md lg xl>
                  {/* TODO: Fix this the next time the file is edited. */}
                  {/* eslint-disable-next-line react/jsx-props-no-spreading */}
                  <StyledShareListingButton iconOnly {...shareProps} />
                </Hide>

                <StyledButton
                  onClick={toggleFullScreen}
                  data-qa-id={
                    fullScreen
                      ? 'idealist-days-map-exit-full-screen'
                      : 'idealist-days-map-go-full-screen'
                  }
                  title={
                    fullScreen
                      ? getText('Exit full screen map')
                      : getText('Expand map to full screen')
                  }
                  aria-label={
                    fullScreen
                      ? getText('Exit full screen map')
                      : getText('Expand map to full screen')
                  }
                >
                  <Box display="flex" alignItems="center">
                    <BodySmall
                      display={['none', null, 'flex']}
                      styleAs="h5"
                      mr="8px"
                      my={0}
                      lineHeight={1}
                    >
                      {fullScreen ? getText('Close map') : getText('Expand')}
                    </BodySmall>
                    <Icon
                      name={
                        fullScreen ? 'fullscreen-collapse' : 'fullscreen-expand'
                      }
                      display="block"
                      width={18}
                      height={18}
                      fill="currentColor"
                    />
                  </Box>
                </StyledButton>
              </Box>

              <Box position="absolute" bottom={PADDING} left={PADDING}>
                {mapType === 'DAYS' ? (
                  <Hide md lg xl>
                    <SelectDateButton
                      date={date}
                      setShowInfoBox={setShowInfoBox}
                      setShowDates={setShowDatesHandler}
                      showInfoBox={showInfoBox}
                    />
                  </Hide>
                ) : (
                  <TrackedPrimaryButtonLink
                    p={16}
                    borderRadius={10}
                    boxShadow="0 1px 4px rgba(0,0,0,0.2)"
                    href="/groups/create"
                    eventName="Idealist Map Create Group"
                  >
                    <Hide lg xl>
                      {getText('Create Group')}
                    </Hide>
                    <Hide xs sm md>
                      {getText('Create Mutual Aid Group')}
                    </Hide>
                  </TrackedPrimaryButtonLink>
                )}
                <Box
                  mt="8px"
                  bg="white"
                  borderRadius={10}
                  boxShadow="0 1px 4px rgba(0,0,0,0.1)"
                >
                  {isDefined(eventOrGroupCount) && (
                    <Box
                      pl={10}
                      pr={40}
                      display="flex"
                      height={52}
                      alignItems="center"
                      borderBottom={
                        isDefined(userPinCount) &&
                        `1px solid ${colors.platinum}`
                      }
                    >
                      <Icon
                        name="map-pin"
                        width={20}
                        height={20}
                        display="block"
                      />
                      <EventOrGroupCount
                        mapType={mapType}
                        eventOrGroupCount={eventOrGroupCount}
                      />
                    </Box>
                  )}
                  {isDefined(userPinCount) && (
                    <Box
                      pl={10}
                      pr={40}
                      display="flex"
                      height={52}
                      alignItems="center"
                    >
                      <StyledImg
                        width={20}
                        height={20}
                        layout="responsive"
                        src="/static/images/logo-mark.png"
                        srcSet="/static/images/logo-mark.png, /static/images/logo-mark@2x.png 2x, /static/images/logo-mark@3x.png 3x"
                        alt="Idealist logo"
                      />
                      <UserPinCount
                        userPinCount={userPinCount}
                        mapType={mapType}
                      />
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          )}
          {showDates && <Dates setDate={setNewDate} activeDate={date} />}
          {loaded && showInfoBox && (
            <Box position="absolute" width={1} height="100%" left={0} top={0}>
              <Box
                position="absolute"
                width={1}
                height="100%"
                left={0}
                top={0}
                bg="rgba(0,0,0,0.3)"
                role="button"
                cursor="pointer"
                onClick={() => setShowInfoBox(false)}
              />
              <InfoBox
                position="absolute"
                top="50%"
                left="50%"
                transform="translate(-50%, -50%)"
                width={[
                  `calc(100% - ${gutterWidth}px)`,
                  null,
                  null,
                  `calc(100% - ${gutterWidth * 2}px)`,
                ]}
                maxWidth={520}
                px={[24, null, null, 48]}
                py={[32, null, null, 48]}
                bg="white"
                borderRadius={12}
                boxShadow="0 2px 20px rgba(0, 0, 0, 0.15)"
                textAlign="center"
                setShowInfoBox={setShowInfoBox}
                selectAddress={onSelectAddress}
                mapType={mapType}
                initialValues={initialValues}
                instructions={instructions}
                buttonText={buttonText}
              />
            </Box>
          )}
        </MapWrapper>
      </Box>
    );
  }
);
export default IdealistDaysMap;
